import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  ExternalLink,
  Calendar,
  AlertTriangle,
  Building2,
  FileText,
  ClipboardCheck,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UrgencyBadge } from '@/components/shared/UrgencyBadge';
import { FrameworkBadge } from '@/components/shared/FrameworkBadge';
import { api } from '@/lib/api';
import type { ChangeEventDetail } from '@/types/api';
import { cn } from '@/lib/utils';

interface ChangeEventDetailPanelProps {
  readonly eventId: string | null;
  readonly onClose: () => void;
}

function formatDocumentType(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(date: string | null | undefined): string {
  if (!date) return 'Not set';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function toNum(v: unknown): number {
  return Number(v) || 0;
}

function calcRiskScore(urgencyScore: number, effectiveDate: string | null): number {
  let multiplier = 1.0;
  if (effectiveDate) {
    const daysLeft = Math.ceil((new Date(effectiveDate).getTime() - Date.now()) / 86_400_000);
    if (daysLeft <= 0) multiplier = 2.0;
    else if (daysLeft <= 14) multiplier = 1.8;
    else if (daysLeft <= 30) multiplier = 1.5;
    else if (daysLeft <= 60) multiplier = 1.2;
  }
  return Math.min(10, urgencyScore * multiplier);
}

function getRiskConfig(score: number) {
  if (score >= 9) return { label: 'Critical Risk', bg: 'bg-compliance-critical/10', border: 'border-compliance-critical', text: 'text-compliance-critical' };
  if (score >= 7) return { label: 'High Risk', bg: 'bg-compliance-high/10', border: 'border-compliance-high', text: 'text-compliance-high' };
  if (score >= 5) return { label: 'Moderate Risk', bg: 'bg-compliance-medium/10', border: 'border-compliance-medium', text: 'text-compliance-medium' };
  return { label: 'Low Risk', bg: 'bg-compliance-low/10', border: 'border-compliance-low', text: 'text-compliance-low' };
}

function generateImpactBullets(detail: ChangeEventDetail): string[] {
  const bullets: string[] = [];
  const score = toNum(detail.urgencyScore);
  const frameworks = Array.isArray(detail.frameworksAffected) ? detail.frameworksAffected : [];
  const cards = Array.isArray(detail.affectedCards) ? detail.affectedCards : [];
  const depts = Array.isArray(detail.affectedDepartments) ? detail.affectedDepartments : [];

  if (score >= 9) {
    bullets.push('Critical compliance action required — review and update affected documents immediately to avoid regulatory risk.');
  } else if (score >= 7) {
    bullets.push('High-priority regulatory change — affected departments should review within the next review cycle.');
  } else {
    bullets.push('Routine regulatory update — review at next scheduled compliance check.');
  }

  if (frameworks.length > 0) {
    bullets.push(`Impacts ${frameworks.join(', ')} framework${frameworks.length > 1 ? 's' : ''} — ensure all referenced policies remain aligned.`);
  }

  if (cards.length > 0) {
    const uniqueDepts = [...new Set(cards.map((c) => c.department))];
    bullets.push(`${cards.length} internal document${cards.length > 1 ? 's' : ''} across ${uniqueDepts.length} department${uniqueDepts.length > 1 ? 's' : ''} may need revision (${formatDocumentType(detail.documentType ?? 'update').toLowerCase()}).`);
  } else if (depts.length > 0) {
    bullets.push(`Affects ${depts.map((d) => formatDocumentType(d)).join(', ')} — verify department-level compliance.`);
  }

  return bullets;
}

export function ChangeEventDetailPanel({ eventId, onClose }: ChangeEventDetailPanelProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [aiBullets, setAiBullets] = useState<string[]>([]);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const lastGeneratedEventId = useRef<string | null>(null);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['changeEventDetail', eventId],
    queryFn: () => api.changeEvents.get(eventId!),
    enabled: !!eventId,
  });

  // Generate AI bullets when detail loads
  useEffect(() => {
    if (!detail || !eventId || lastGeneratedEventId.current === eventId) return;
    lastGeneratedEventId.current = eventId;
    setIsGeneratingAI(true);
    setAiBullets([]);

    api.ai
      .explainRegulation({
        documentType: detail.documentType,
        sourceName: detail.sourceName,
        frameworksAffected: [...detail.frameworksAffected],
        urgencyScore: toNum(detail.urgencyScore),
        effectiveDate: detail.effectiveDate,
        affectedDepartments: [...detail.affectedDepartments],
        affectedCardCount: detail.affectedCardCount,
      })
      .then((res) => setAiBullets(res.bullets))
      .catch(() => setAiBullets(generateImpactBullets(detail)))
      .finally(() => setIsGeneratingAI(false));
  }, [detail, eventId]);

  // Reset on close
  useEffect(() => {
    if (!eventId) {
      lastGeneratedEventId.current = null;
      setAiBullets([]);
    }
  }, [eventId]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (eventId) {
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
    }
  }, [eventId, onClose]);

  if (!eventId) return null;

  const score = detail ? toNum(detail.urgencyScore) : 0;
  const isPastDue = detail?.effectiveDate
    ? new Date(detail.effectiveDate) < new Date()
    : false;
  const frameworks = detail && Array.isArray(detail.frameworksAffected) ? detail.frameworksAffected : [];
  const cards = detail && Array.isArray(detail.affectedCards) ? detail.affectedCards : [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-border bg-background shadow-xl transition-transform duration-300 ease-out"
        style={{ transform: eventId ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Change Event Detail
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-6 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : detail ? (
            <>
              {/* Title */}
              <div>
                <h3 className="text-lg font-semibold leading-snug text-foreground">
                  {formatDocumentType(detail.documentType ?? 'Unknown')}: {detail.sourceName ?? 'Unknown source'}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {formatDocumentType(detail.documentType ?? 'Unknown')}
                  </span>
                  {detail.sourceUrl && (
                    <a
                      href={detail.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-teal hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Source
                    </a>
                  )}
                </div>
              </div>

              {/* Compliance Risk Rating */}
              {(() => {
                const riskScore = calcRiskScore(score, detail.effectiveDate);
                const riskConfig = getRiskConfig(riskScore);
                return (
                  <div className={cn('rounded-lg border p-3', riskConfig.bg, riskConfig.border)}>
                    <div className="flex items-center justify-between">
                      <span className={cn('text-xs font-bold uppercase tracking-wider', riskConfig.text)}>
                        {riskConfig.label}
                      </span>
                      <span className={cn('text-lg font-bold', riskConfig.text)}>
                        {riskScore.toFixed(1)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Urgency {score.toFixed(1)} {detail.effectiveDate ? '× deadline proximity' : '(no deadline set)'}
                    </p>
                  </div>
                );
              })()}

              {/* Effective date */}
              <div className="flex items-center gap-2">
                <Calendar className={cn('h-4 w-4', isPastDue ? 'text-compliance-critical' : 'text-muted-foreground')} />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Effective Date</p>
                  <p className={cn('text-sm font-semibold', isPastDue ? 'text-compliance-critical' : 'text-foreground')}>
                    {formatDate(detail.effectiveDate)}
                    {isPastDue && <span className="ml-1.5 text-xs font-normal">(past due)</span>}
                  </p>
                </div>
              </div>

              {/* Regulatory frameworks */}
              {frameworks.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Regulatory Frameworks</p>
                  <div className="flex flex-wrap gap-1.5">
                    {frameworks.map((fw) => (
                      <FrameworkBadge key={fw} framework={fw} />
                    ))}
                  </div>
                </div>
              )}

              {/* Affected departments / cards */}
              {cards.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" />
                    Affected Documents ({cards.length})
                  </p>
                  <div className="space-y-1.5">
                    {cards.map((card) => (
                      <div
                        key={card.cardId}
                        className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-foreground">{card.documentTitle}</p>
                          <p className="text-xs text-muted-foreground">{formatDocumentType(card.department)}</p>
                        </div>
                        <UrgencyBadge score={toNum(card.urgencyScore)} className="ml-2 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Why this matters */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-compliance-high" />
                  Why This Matters
                </p>
                {isGeneratingAI ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }, (_, i) => (
                      <div key={i} className="h-4 animate-pulse rounded bg-muted" />
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {(aiBullets.length > 0 ? aiBullets : generateImpactBullets(detail)).map((bullet, i) => (
                      <li key={i} className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
                {aiBullets.length > 0 && !isGeneratingAI && (
                  <p className="mt-2 text-[10px] text-muted-foreground/60">AI-generated analysis</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 bg-teal hover:bg-teal-dark text-white"
                  onClick={() => {
                    onClose();
                    navigate('/review');
                  }}
                >
                  <ClipboardCheck className="mr-2 h-4 w-4" />
                  Open Review Queue
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    onClose();
                    navigate('/archive');
                  }}
                >
                  <Archive className="mr-2 h-4 w-4" />
                  View in Archive
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              Event not found
            </div>
          )}
        </div>
      </div>
    </>
  );
}
